<script setup>
import { ref, onMounted, nextTick, computed, watch } from 'vue';
import {
  applyRibbonEmptyPhoneAsNull,
  getRibbonMandatoryPhoneError,
  resolveRibbonPhoneConfig,
  syncRibbonPhoneFormField,
} from '~/utils/ribbon-phone-payload'

const props = defineProps({
  data: Object,
  forceMobileMode: Boolean
})
const { validateWithZeroBounce, getFullWidthRibbonFormFields, getSharedFullWidthRibbon, leadGenData, sanitizeLeadGenPayload, processLeadSubmission } = useLeadGenData();
const emailField = ref(false);

const sharedData = ref(null)
const leadGenFields = ref(null)
const formData = ref(getFullWidthRibbonFormFields());
const brochureRequestStore = useBrochureRequest();
const mandatoryError = ref('')

const { data: fetchedSharedData } = await getSharedFullWidthRibbon();
if(props.data.sharedContent) {
  sharedData.value = fetchedSharedData.value.data.attributes.formConfiguration;
} else {
  sharedData.value = props.data.fullWidthRibbon;
}

const phoneConfig = computed(() => resolveRibbonPhoneConfig(props.data?.phoneNumberField))

// Keep form field required/valid flags in sync with CMS settings
watch(
  phoneConfig,
  (config) => {
    syncRibbonPhoneFormField(formData.value.phoneNumber, config)
  },
  { immediate: true }
)

const { data: leadGen } = await leadGenData();
leadGenFields.value = leadGen.value.leadGenFields;
const formIsValid = ref(false);
const requestSent = ref(false)
const ribbonRef = ref(null)

const btnSubmitClass = computed(() => {
  return requestSent.value ? 'disabled' : ''
})

const buttonText = computed(() => {
  return requestSent.value ? 'Sending...' : sharedData.value.primaryCTA.buttonText;
})

const onFieldChanged = (field) => {  
  for (const key in formData.value) {
    // Saving all the fields data while they are being updated    
    if (field.id === key) {      
      formData.value[key].value = field.value;
      formData.value[key].valid = field.valid;
    }    
  }
  // Clear mandatory error once the user fills the phone field
  if (field.id === 'phoneNumber' && field.valid) {
    mandatoryError.value = ''
  }
}

function isObject(objValue) {
    return objValue && typeof objValue === 'object' && objValue.constructor === Object;
  }

const handleSubmit = async () => {
  mandatoryError.value = ''
  ribbonRef.value?.validateFields?.()
  await nextTick()
  if (!formData.value.firstName.valid || !formData.value.emailAddress.valid) {
    return;
  }
  const mandatoryPhoneError = getRibbonMandatoryPhoneError(
    phoneConfig.value.isMandatory,
    formData.value.phoneNumber.valid,
  )
  if (mandatoryPhoneError) {
    mandatoryError.value = mandatoryPhoneError
    return
  }
  requestSent.value = true;
  const payload = sanitizeLeadGenPayload(formData.value);
  payload.sourcePageUrl = window?.location?.href;
  // Hash before setting null — keep shared hashUtility unchanged for other forms
  const hashedData = hashObject(payload); 
  dataLayer.push({
    'event': 'formSubmitted',
    'leadsUserData': hashedData
  });
  // Visible + empty → null; hidden → omit key (pre–phone-field behavior)
  applyRibbonEmptyPhoneAsNull(payload, phoneConfig.value.isVisible)

  if (sharedData.value.zeroBounce) {  
    const encodedEmail = encodeURIComponent(payload.emailAddress)        
    const { data } = await validateWithZeroBounce(encodedEmail)
    if (data.value.status === 'invalid' ) {
      emailField.value = true;
      requestSent.value = false;
      return;
    }
  } 
  if ((sharedData.value && (sharedData.value.tagsArray && sharedData.value.tagsArray !== null && sharedData.value.tagsArray.length > 0))) { 
    const tagsArray = sharedData.value.tagsArray
    const tags = []
    if (isObject(tagsArray)) {
      for (let i = 0; i < tagsArray.length; i++) {
        tags.push(tagsArray[i].tag)
      }
    } else {
      for (let i = 0; i < tagsArray.length; i++) {
        if (isObject(tagsArray[i])) {
          tags.push(tagsArray[i].tag)
        } else {
          tags.push(tagsArray[i])
        }
      }
    }
    payload['tagsArray'] = tags
  }

  // Process the lead submission
  const apiResponse = await processLeadSubmission(payload)
  formSubmitted.value = apiResponse?.status == 200 || false  
}
watch(
  formData,
  (newFormData) => {
    const phoneOk = !phoneConfig.value.isMandatory || newFormData.phoneNumber.valid
    formIsValid.value = newFormData.emailAddress.valid && newFormData.firstName.valid && phoneOk
  },
  { deep: true } // Deep watch to track changes in nested properties
);
const formSubmitted = ref(false)

onMounted(() => {
  const form = document.getElementById('lead-full-width-ribbon');
  if (form) {
    const fieldsets = form.getElementsByTagName('fieldset');
    if (fieldsets[0]) {
      fieldsets[0].removeAttribute('disabled');
    }
  }
})
</script>

<template>
  <Ribbon
    ref="ribbonRef"
    :formSubmitted="formSubmitted"
    :sharedData="sharedData"
    :leadGenFields="leadGenFields"
    :brochureRequestStore="brochureRequestStore"
    :buttonText="buttonText"
    :btnSubmitClass="btnSubmitClass"
    :forceMobileMode="props.forceMobileMode"
    :emailField="emailField"
    :phoneConfig="phoneConfig"
    :mandatoryError="mandatoryError"
    @onFieldChanged="onFieldChanged"
    @handleSubmit="handleSubmit"
    >
    </Ribbon>
</template>

<style lang="scss">
.full-width-ribbon {
  color: #1F1F1F;

  .social-share-container {
    width: 100%;
    max-width: 420px;
    background: $color-secondary;
    padding: 40px;
    border: solid 1px $color-light-gray;
    border-radius: 24px;
    margin: 20px;
  }
}
</style>
